import React, { useCallback, useContext, useEffect, useState } from 'react';
import { Alert, Button, Card, Form, Modal, Table } from 'react-bootstrap';
import axios from '../../../../api/axios';
import { GlobalContext } from '../../../../store/GlobalContext';

type DeliveryBoy = {
  _id?: string;
  name: string;
  phoneNumber?: string | null;
};

const DeliveryBoys = () => {
  const { state } = useContext(GlobalContext);
  const token = state?.auth?.token;

  const [rows, setRows] = useState<DeliveryBoy[]>([]);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showDialog, setShowDialog] = useState(false);
  const [editingRow, setEditingRow] = useState<DeliveryBoy | null>(null);
  const [form, setForm] = useState({
    name: '',
    phoneNumber: '',
  });

  const fetchRows = useCallback(async () => {
    if (!token) return;

    try {
      const response = await axios.get('/users/delivery-boys/list', {
        headers: { Authorization: `Bearer ${token}` },
      });
      const list = Array.isArray(response.data) ? response.data : [];
      setRows(list);
    } catch (err: any) {
      if (err?.response?.status === 404) {
        try {
          const fallback = await axios.get('/users/delivery-boys', {
            headers: { Authorization: `Bearer ${token}` },
          });
          const list = Array.isArray(fallback.data) ? fallback.data : [];
          setRows(list);
          return;
        } catch (fallbackErr: any) {
          setError(fallbackErr?.response?.data?.message || 'Failed to load delivery boys.');
          return;
        }
      }

      setError(err?.response?.data?.message || 'Failed to load delivery boys.');
    }
  }, [token]);

  useEffect(() => {
    fetchRows();
  }, [fetchRows]);

  const resetForm = () => {
    setForm({ name: '', phoneNumber: '' });
    setEditingRow(null);
  };

  const openCreateDialog = () => {
    setError('');
    setSuccess('');
    resetForm();
    setShowDialog(true);
  };

  const openEditDialog = (row: DeliveryBoy) => {
    setError('');
    setSuccess('');
    setEditingRow(row);
    setForm({
      name: row.name || '',
      phoneNumber: row.phoneNumber || '',
    });
    setShowDialog(true);
  };

  const onSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setError('');
    setSuccess('');

    if (!form.name.trim()) {
      setError('Name is required.');
      return;
    }

    try {
      if (editingRow?._id) {
        const payload: Record<string, string> = {
          name: form.name.trim(),
          phoneNumber: form.phoneNumber.trim(),
        };

        await axios.put(`/users/delivery-boys/${editingRow._id}`, payload, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setSuccess('Delivery boy updated successfully.');
      } else {
        await axios.post('/users/delivery-boys', {
          name: form.name.trim(),
          phoneNumber: form.phoneNumber.trim(),
        }, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setSuccess('Delivery boy created successfully.');
      }

      setShowDialog(false);
      resetForm();
      await fetchRows();
    } catch (err: any) {
      setError(err?.response?.data?.message || 'Failed to save delivery boy.');
    }
  };

  const handleDelete = async (row: DeliveryBoy) => {
    if (!row._id) return;

    if (!window.confirm(`Delete delivery boy ${row.name}?`)) {
      return;
    }

    setError('');
    setSuccess('');
    try {
      await axios.delete(`/users/delivery-boys/${row._id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setSuccess('Delivery boy deleted successfully.');
      await fetchRows();
    } catch (err: any) {
      setError(err?.response?.data?.message || 'Failed to delete delivery boy.');
    }
  };

  return (
    <>
      <Card className="p-3">
        <div className="d-flex justify-content-between align-items-center mb-3">
          <h4 className="mb-0">Delivery Boys</h4>
          <Button variant="dark" onClick={openCreateDialog}>Add Delivery Boy</Button>
        </div>

        {error && <Alert variant="danger">{error}</Alert>}
        {success && <Alert variant="success">{success}</Alert>}

        <Table striped bordered hover responsive>
          <thead>
            <tr>
              <th>Name</th>
              <th>Phone Number</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 ? (
              <tr>
                <td colSpan={3} className="text-center">No delivery boys found.</td>
              </tr>
            ) : (
              rows.map((row) => (
                <tr key={row._id ?? row.name}>
                  <td>{row.name}</td>
                  <td>{row.phoneNumber || '-'}</td>
                  <td>
                    <Button size="sm" variant="warning" className="me-2" onClick={() => openEditDialog(row)}>Edit</Button>
                    <Button size="sm" variant="danger" onClick={() => handleDelete(row)}>Delete</Button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </Table>
      </Card>

      <Modal show={showDialog} onHide={() => { setShowDialog(false); resetForm(); }}>
        <Modal.Header closeButton>
          <Modal.Title>{editingRow ? 'Edit Delivery Boy' : 'Add New Delivery Boy'}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form onSubmit={onSubmit}>
            <Form.Group className="mb-2">
              <Form.Label>Name</Form.Label>
              <Form.Control value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required />
            </Form.Group>

            <Form.Group className="mb-2">
              <Form.Label>Phone Number (optional)</Form.Label>
              <Form.Control value={form.phoneNumber} onChange={(e) => setForm({ ...form, phoneNumber: e.target.value })} />
            </Form.Group>

            <div className="d-flex justify-content-end gap-2">
              <Button variant="secondary" onClick={() => { setShowDialog(false); resetForm(); }}>Cancel</Button>
              <Button type="submit" variant="dark">{editingRow ? 'Update Delivery Boy' : 'Create Delivery Boy'}</Button>
            </div>
          </Form>
        </Modal.Body>
      </Modal>
    </>
  );
};

export default DeliveryBoys;
